import { createContext, type ReactNode, use, useMemo, useState } from "react";

export type VatMode = "without" | "with";

export type CostDisplayState = {
  readonly showPrices: boolean;
  readonly setShowPrices: (value: boolean) => void;
  readonly vatMode: VatMode;
  readonly setVatMode: (value: VatMode) => void;
};

// App-wide display preference for recipe cost columns: whether prices are
// shown and in which VAT mode. Lives above the router so it survives
// navigation between recipes and view/edit switches.
const CostDisplayContext = createContext<CostDisplayState>({
  showPrices: false,
  setShowPrices: () => {},
  vatMode: "without",
  setVatMode: () => {},
});

export const CostDisplayProvider = ({ children }: { readonly children: ReactNode }) => {
  const [showPrices, setShowPrices] = useState(false);
  const [vatMode, setVatMode] = useState<VatMode>("without");
  const value = useMemo(() => ({ showPrices, setShowPrices, vatMode, setVatMode }), [showPrices, vatMode]);
  return <CostDisplayContext value={value}>{children}</CostDisplayContext>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCostDisplay = (): CostDisplayState => use(CostDisplayContext);
